import type React from 'react';
import { useEffect, useRef } from 'react';
import { Card } from 'react-bootstrap';
import * as echarts from 'echarts';
import { ToggleButton } from '../PromptCommonFunctions';

interface HorizontalBarChartProps {
  HorizontalBarChartData: any; // replace `any` with proper type later
  selectedButtonIndex?: number;
  buttonNames?: string[];
  handleClick?: () => void;
}

// @ts-ignore
const HorizontalBarChart: React.FC<HorizontalBarChartProps> = ({
  HorizontalBarChartData,
  selectedButtonIndex = 0,
  buttonNames = [],
  handleClick = () => {}
}) => {
  const chartRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!chartRef.current) return;
    const chart = echarts.init(chartRef.current);
    const option = {
      title: {
        text: HorizontalBarChartData?.title,
        subtext: HorizontalBarChartData?.subtitle,
        left: 0,
        top: 0,
        textStyle: {
          fontSize: 20,
          fontWeight: 400,
          color: '#18181B',
          lineHeight: 16
        },
        subtextStyle: {
          fontSize: 14,
          color: '#71717A',
          fontWeight: 400,
          lineHeight: 16
        }
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: { type: 'shadow' }
      },
      grid: {
        bottom: '8%'
      },
      xAxis: {
        type: 'value',
        name: HorizontalBarChartData?.xAxis.name,
        nameLocation: 'middle',
        nameGap: 40,
        axisLabel: {
          fontSize: 12,
          color: '#4B4B53',
          fontWeight: 400
        },
        nameTextStyle: {
          color: '#18181B',
          fontSize: 16,
          fontWeight: 400
        },
        splitLine: {
          show: true,
          lineStyle: {
            type: 'dashed',
            color: 'rgba(0, 102, 204, 0.25)'
          }
        }
      },
      yAxis: {
        type: 'category',
        data: HorizontalBarChartData?.yAxis?.categories,
        inverse: true, // keeps biggest at top
        name: HorizontalBarChartData?.yAxis?.name,
        nameLocation: 'middle',
        nameTextStyle: {
          color: '#18181B',
          fontSize: 16,
          fontWeight: 400
        },
        axisLabel: {
          fontSize: 12,
          color: '#4B4B53',
          fontWeight: 400
        },
        splitLine: {
          show: true,
          lineStyle: {
            type: 'dashed',
            color: 'rgba(0, 102, 204, 0.25)'
          }
        }
      },
      series: [
        {
          type: 'bar',
          data: HorizontalBarChartData?.series?.data,
          label: {
            show: true,
            position: 'right'
          },
          itemStyle: {
            color: '#0066cc'
          },
          barWidth: '60%',
          barMaxWidth: 50
        }
      ]
    };

    chart.setOption(option);

    const handleResize = () => {
      chart.resize();
    };

    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
      chart.dispose();
    };
  }, [HorizontalBarChartData]);

  return (
    <Card className="h-100 shadow-sm">
      {!!buttonNames?.length && (
        <div className="d-flex justify-content-end">
          <ToggleButton
            selectedButtonIndex={selectedButtonIndex}
            buttonNames={buttonNames}
            handleClick={handleClick}
          />
        </div>
      )}
      <Card.Body className="p-3">
        <div ref={chartRef} style={{ width: '100%', height: '400px' }} />
      </Card.Body>
    </Card>
  );
};

export default HorizontalBarChart;
